import {
  GridApiPremium,
  gridFilteredSortedRowIdsSelector,
  GridRowId,
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useCallback } from 'react';
import { ItemPriority } from '@retail/products/types';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';

interface ActionProps {
  apiRef: MutableRefObject<GridApiPremium | null>;
  assortmentItems: AssortmentItemMinimal[];
  duplicateRowIds: number[];
  setDuplicateRowIds: (ids: number[]) => void;
}

const useItemEditActions = ({
  apiRef,
  assortmentItems,
  duplicateRowIds,
  setDuplicateRowIds,
}: ActionProps) => {
  const handleDateChange = useCallback(
    (dateType: 'validFrom' | 'validTo', rowId: GridRowId, newDate: Date | null) => {
      const selectedRow = apiRef.current?.getRow(rowId);
      apiRef.current?.updateRows([
        {
          mgSupplierItemId: selectedRow.mgSupplierItemId,
          validFrom: dateType === 'validFrom' ? newDate : selectedRow.validFrom,
          validTo: dateType === 'validTo' ? newDate : selectedRow.validTo,
        },
      ]);
    },
    [apiRef]
  );

  const handlePriorityChange = useCallback(
    (rowId: GridRowId) => {
      const selectedRow = apiRef.current?.getRow(rowId);
      const supplierItems = assortmentItems.filter(
        (item) => item.mgItemNumber === selectedRow.mgItemNumber
      );
      if (supplierItems.length > 0) {
        const supplierItemWithMainPrio = supplierItems.filter(
          (item) => !!apiRef.current?.getRow(item.mgSupplierItemId).priority.prioritised
        );
        if (
          supplierItemWithMainPrio.length > 2 ||
          (!selectedRow.priority.prioritised &&
            (supplierItemWithMainPrio.length === 1 || supplierItemWithMainPrio.length === 2))
        ) {
          setDuplicateRowIds([...duplicateRowIds, selectedRow.mgItemNumber]);
        } else {
          setDuplicateRowIds(duplicateRowIds.filter((id) => id !== selectedRow.mgItemNumber));
        }
      }

      apiRef.current?.updateRows([
        {
          mgSupplierItemId: selectedRow.mgSupplierItemId,
          priority: { prioritised: !selectedRow.priority.prioritised },
        },
      ]);
    },
    [apiRef, assortmentItems, duplicateRowIds, setDuplicateRowIds]
  );

  const handleStockedChange = useCallback(
    (rowId: GridRowId, isStocked: boolean) => {
      const selectedRow = apiRef.current?.getRow(rowId);
      apiRef.current?.updateRows([
        {
          mgSupplierItemId: selectedRow.mgSupplierItemId,
          isStocked: !isStocked,
        },
      ]);
    },
    [apiRef]
  );

  const handleBulkChangeDates = useCallback(
    (dateType: 'validFrom' | 'validTo', newDate: Date | null) => {
      const rowIds = gridFilteredSortedRowIdsSelector(apiRef) ?? [];
      dateType === 'validFrom' &&
        apiRef.current?.updateRows(
          rowIds.map((rowId) => ({ mgSupplierItemId: rowId, validFrom: newDate }))
        );
      dateType === 'validTo' &&
        apiRef.current?.updateRows(
          rowIds.map((rowId) => ({ mgSupplierItemId: rowId, validTo: newDate }))
        );
    },
    [apiRef]
  );

  const handleBulkChangePriority = useCallback(
    (prio: ItemPriority) => {
      const rowIds = gridFilteredSortedRowIdsSelector(apiRef) ?? [];

      const itemNumbers = assortmentItems
        .filter((item) => rowIds.includes(item.mgSupplierItemId))
        .map((item) => item.mgItemNumber);
      const uniqueNumbers = new Set(itemNumbers);
      const duplicates = itemNumbers.filter((id) => {
        if (uniqueNumbers.has(id)) {
          uniqueNumbers.delete(id);
          return null;
        }
        return id;
      });
      prio === ItemPriority.TEN ? setDuplicateRowIds(duplicates) : setDuplicateRowIds([]);
      apiRef.current?.updateRows(
        rowIds.map((rowId) => ({
          mgSupplierItemId: rowId,
          priority: { prioritised: prio === ItemPriority.TEN },
        }))
      );
    },
    [apiRef, assortmentItems, setDuplicateRowIds]
  );

  const handleBulkChangeStockedStatus = useCallback(
    (isStocked: boolean) => {
      const rowIds = gridFilteredSortedRowIdsSelector(apiRef) ?? [];

      apiRef.current?.updateRows(
        rowIds.map((rowId) => {
          return {
            mgSupplierItemId: rowId,
            isStocked,
          };
        })
      );
    },
    [apiRef]
  );

  return {
    handleDateChange,
    handlePriorityChange,
    handleStockedChange,
    handleBulkChangeDates,
    handleBulkChangePriority,
    handleBulkChangeStockedStatus,
  };
};

export default useItemEditActions;
